import Phaser from 'phaser';
import { GAME_FONT_FAMILY } from '@shared/constants';
import { getResourceNodeType, getNodeProduceEntries } from '@shared/data/resourceNodes';
import { getItemTemplate } from '@shared/data/items';
import { getSkillDef } from '@shared/data/skills';
import type { ResourceNodeStateView } from '../../state/GameStateView';

/**
 * Tooltip flotante para NODOS DE RECURSO (cardúmenes de pesca, yacimientos de minería, árboles de
 * tala). Se muestra al hacer HOVER sostenido sobre un nodo (dwell de ~1s, ver
 * ResourceNodeRenderer) con: qué item(s) da y su probabilidad, el nivel de skill requerido y la
 * cantidad TOTAL del recurso (no la restante).
 *
 * Data-driven: todo sale de `getResourceNodeType(typeId)` (nombre/skill/nivel/tabla `produces`) +
 * `getItemTemplate(itemId)` (ícono/nombre del pez/mena/madera) + el `maxQuantity` sincronizado del
 * state. Modelado en ItemTooltip (bg + título + filas ícono/texto + layout con clamp al viewport,
 * scrollFactor 0), pero acá el ícono va A LA IZQUIERDA de cada fila de item.
 *
 * Los cardúmenes con tabla `produces` (varias especies) listan una fila por especie con su `%`; los
 * nodos simples (una sola cosa al 100%) muestran el ícono + nombre sin porcentaje.
 */
const DEPTH = 1300;
const PADDING = 8;
const MAX_WIDTH = 240;
const ROW_H = 16;
const ICON_SIZE = 14;
const ICON_GAP = 5;

/** Acento del título por skill del nodo. */
const SKILL_COLOR: Record<string, string> = {
  skill_fishing: '#7fd6ff',
  skill_mining: '#d6b98f',
  skill_woodcutting: '#a6d688',
};

function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return { fontFamily: GAME_FONT_FAMILY, resolution: 2, ...style } as T;
}

/** Una fila del cuerpo: [ícono opcional] texto (con color propio). */
interface Row {
  iconKey?: string; // texture key ya resuelta (item_icon_<x>) o undefined
  text: string;
  color?: string;
}

export class ResourceNodeTooltip {
  private readonly scene: Phaser.Scene;
  private bg!: Phaser.GameObjects.Rectangle;
  private title!: Phaser.GameObjects.Text;
  private body!: Phaser.GameObjects.Container;
  private bodyWidth = 0;
  private bodyHeight = 0;
  private isVisible = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.build();
    this.hide();
  }

  private build(): void {
    this.bg = this.scene.add
      .rectangle(0, 0, 100, 100, 0x000000, 0.92)
      .setOrigin(0, 0)
      .setStrokeStyle(1, 0x888888)
      .setDepth(DEPTH)
      .setScrollFactor(0);
    this.title = this.scene.add
      .text(0, 0, '', withGameFont({
        fontSize: '12px', color: '#ffff88', fontStyle: 'bold',
        wordWrap: { width: MAX_WIDTH },
      }))
      .setOrigin(0, 0)
      .setDepth(DEPTH + 1)
      .setScrollFactor(0);
    this.body = this.scene.add.container(0, 0, []).setDepth(DEPTH + 1).setScrollFactor(0);
  }

  /** Resuelve la texture key del ícono de un item (con fallback a noicon). */
  private itemIconKey(itemId: string): string {
    const iconName = getItemTemplate(itemId)?.icon;
    const key = iconName ? `item_icon_${iconName}` : 'item_icon_noicon';
    return this.scene.textures.exists(key) ? key : 'item_icon_noicon';
  }

  /**
   * Muestra el tooltip del nodo dado en la posición (x, y) del pointer (coords de pantalla).
   */
  show(node: ResourceNodeStateView, pointerX: number, pointerY: number): void {
    const type = getResourceNodeType(node.typeId);
    if (!type) {
      this.hide();
      return;
    }
    const accent = SKILL_COLOR[type.skill] ?? '#ffff88';
    this.title.setText(type.name).setColor(accent);

    const entries = getNodeProduceEntries(type);
    const multi = entries.length > 1;
    const rows: Row[] = [];
    // Una fila por item que da el nodo: [ícono] Nombre — NN% (el % sólo si hay tabla de varios).
    for (const e of entries) {
      const name = getItemTemplate(e.itemId)?.name ?? e.itemId;
      const pct = multi ? `  ${Math.round(e.chance * 100)}%` : '';
      rows.push({ iconKey: this.itemIconKey(e.itemId), text: `${name}${pct}`, color: '#cfe8ff' });
    }
    // % de éxito base del yacimiento (minería): cuánto puede fallar un intento. No incluye el
    // bonus del player (mods/nodo del Enano); es el % base del nodo. Ver docs/MINING_AND_SMITHING.md.
    if (type.successChance != null && type.successChance < 1) {
      rows.push({ text: `Éxito al minar: ${Math.round(type.successChance * 100)}%`, color: '#88d0a0' });
    }
    // Nivel de skill requerido + cantidad TOTAL del recurso (no la restante).
    const skillName = getSkillDef(type.skill)?.name ?? 'la skill';
    rows.push({ text: `Nivel de ${skillName}: ${type.requiredLevel}`, color: '#e0c060' });
    rows.push({ text: `Cantidad: ${node.maxQuantity}`, color: '#aaaaaa' });

    this.rebuildBody(rows);
    this.layout(pointerX, pointerY);
    this.setAllVisible(true);
    this.isVisible = true;
  }

  hide(): void {
    if (!this.isVisible && !this.bg) return;
    this.setAllVisible(false);
    this.isVisible = false;
  }

  destroy(): void {
    this.bg?.destroy();
    this.title?.destroy();
    this.body?.destroy();
  }

  private rebuildBody(rows: Row[]): void {
    this.body.removeAll(true);
    let maxW = 0;
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      const y = i * ROW_H;
      let cursorX = 0;
      if (row.iconKey) {
        const sprite = this.scene.add
          .sprite(cursorX, y + ROW_H / 2, row.iconKey)
          .setOrigin(0, 0.5)
          .setDisplaySize(ICON_SIZE, ICON_SIZE)
          .setScrollFactor(0);
        this.body.add(sprite);
        cursorX += ICON_SIZE + ICON_GAP;
      }
      const text = this.scene.add
        .text(cursorX, y + ROW_H / 2, row.text, withGameFont({
          fontSize: '10px', color: row.color ?? '#cccccc',
          wordWrap: { width: MAX_WIDTH - cursorX },
        }))
        .setOrigin(0, 0.5)
        .setScrollFactor(0);
      this.body.add(text);
      maxW = Math.max(maxW, cursorX + text.width);
    }
    this.bodyWidth = maxW;
    this.bodyHeight = rows.length * ROW_H;
  }

  private layout(pointerX: number, pointerY: number): void {
    const titleH = this.title.height;
    const gap = 4;
    const innerH = titleH + gap + this.bodyHeight;
    const innerW = Math.max(this.title.width, this.bodyWidth, 80);
    const w = innerW + PADDING * 2;
    const h = innerH + PADDING * 2;

    const camW = this.scene.scale.width;
    const camH = this.scene.scale.height;
    let x = pointerX + 14;
    let y = pointerY + 14;
    if (x + w > camW) x = pointerX - w - 14;
    if (y + h > camH) y = camH - h - 4;
    if (x < 4) x = 4;
    if (y < 4) y = 4;

    this.bg.setPosition(x, y).setSize(w, h);
    this.title.setPosition(x + PADDING, y + PADDING);
    this.body.setPosition(x + PADDING, y + PADDING + titleH + gap);
  }

  private setAllVisible(visible: boolean): void {
    this.bg.setVisible(visible);
    this.title.setVisible(visible);
    this.body.setVisible(visible);
  }
}
